<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import Typography from '@tiptap/extension-typography'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Youtube from '@tiptap/extension-youtube'
// Details extension removed — requires detailsContent + detailsSummary sub-extensions which are v2 only

const TEXT_COLORS = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#cccccc',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#6366f1',
  '#a855f7',
]

const HIGHLIGHT_COLORS = [
  '#fef08a',
  '#bbf7d0',
  '#bfdbfe',
  '#e9d5ff',
  '#fecaca',
  '#fed7aa',
  '#d1fae5',
  '#dbeafe',
  '#fce7f3',
  '#f5f5f4',
]

const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier', value: '"Courier New", monospace' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Times', value: '"Times New Roman", serif' },
]

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    error?: string
  }>(),
  { placeholder: 'Begin typing your message here...' },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const showTextColorPicker = ref(false)
const showHighlightColorPicker = ref(false)
const showFontFamilyPicker = ref(false)

// HTML source mode — restored from the legacy Redactor toolbar's "html"
// button (deleted in chore(messaging): remove legacy messages MVC code,
// commit 1d01f3c51). Lets users paste/edit raw HTML email templates that
// the WYSIWYG schema would otherwise normalize.
const sourceMode = ref(false)
const sourceHtml = ref('')

// Ctrl/Cmd-K binding for hyperlink — TipTap's Link extension does not
// register this shortcut by default; the legacy Redactor editor did.
const promptForLink = (editor: ReturnType<typeof useEditor>['value']) => {
  if (!editor) return false
  const previousUrl = editor.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)
  if (url === null) return false
  if (url === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    return true
  }
  editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  return true
}

const LinkWithShortcut = Link.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-k': () => promptForLink(this.editor),
    }
  },
})

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit,
    LinkWithShortcut.configure({ openOnClick: false }),
    Underline,
    Highlight.configure({ multicolor: true }),
    Subscript,
    Superscript,
    TextStyle,
    Color,
    FontFamily,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Placeholder.configure({ placeholder: props.placeholder }),
    CharacterCount,
    Typography,
    Image.configure({ inline: true }),
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader,
    TaskList,
    TaskItem.configure({ nested: true }),
    Youtube.configure({ inline: false }),
  ],
  editorProps: {
    attributes: {
      class: 'prose prose-a:text-blue-600 prose-a:font-normal max-w-none px-4 py-3 min-h-[12rem] focus:outline-none',
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
})

watch(
  () => props.modelValue,
  (value) => {
    if (editor.value && editor.value.getHTML() !== value) {
      editor.value.commands.setContent(value, { emitUpdate: false })
    }
  },
)

onBeforeUnmount(() => {
  editor.value?.destroy()
})

const setLink = () => {
  promptForLink(editor.value)
}

// Source-mode toggle. Pulls current HTML out of TipTap when entering, and
// reapplies edited HTML when leaving. v-model stays in sync via @input on
// the textarea so sending the message uses the source-edited HTML even
// without first switching back to WYSIWYG.
const toggleSourceMode = () => {
  if (!editor.value) return
  if (sourceMode.value) {
    editor.value.commands.setContent(sourceHtml.value || '<p></p>')
    sourceMode.value = false
  } else {
    sourceHtml.value = editor.value.getHTML()
    sourceMode.value = true
  }
}

const onSourceInput = () => {
  emit('update:modelValue', sourceHtml.value)
}

const addImage = () => {
  if (!editor.value) return
  const url = window.prompt('Image URL')
  if (url) {
    editor.value.chain().focus().setImage({ src: url }).run()
  }
}

const addYoutube = () => {
  if (!editor.value) return
  const url = window.prompt('YouTube URL')
  if (url) {
    editor.value.chain().focus().setYoutubeVideo({ src: url }).run()
  }
}

const setTextColor = (color: string) => {
  if (!editor.value) return
  editor.value.chain().focus().setColor(color).run()
  showTextColorPicker.value = false
}

const setHighlightColor = (color: string) => {
  if (!editor.value) return
  editor.value.chain().focus().toggleHighlight({ color }).run()
  showHighlightColorPicker.value = false
}

const setFontFamily = (font: string) => {
  if (!editor.value) return
  if (font === '') {
    editor.value.chain().focus().unsetFontFamily().run()
  } else {
    editor.value.chain().focus().setFontFamily(font).run()
  }
  showFontFamilyPicker.value = false
}

const insertTable = () => {
  if (!editor.value) return
  editor.value.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
}

const btn = 'rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-700'
const btnActive = 'bg-gray-200 text-gray-900'
</script>

<template>
  <div>
    <div class="overflow-hidden rounded-md border" :class="error ? 'border-red-300' : 'border-gray-300'">
      <!-- Toolbar -->
      <div v-if="editor" class="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <!-- Font Family -->
        <div class="relative">
          <button type="button" :class="btn" @click="showFontFamilyPicker = !showFontFamilyPicker" title="Font Family">
            Aa
          </button>
          <div
            v-if="showFontFamilyPicker"
            class="absolute left-0 top-full z-10 mt-1 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            <button
              v-for="font in FONT_FAMILIES"
              :key="font.value"
              type="button"
              class="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
              :style="font.value ? { fontFamily: font.value } : {}"
              @click="setFontFamily(font.value)"
            >
              {{ font.label }}
            </button>
          </div>
        </div>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- Text formatting -->
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('bold') }]"
          class="font-bold"
          @click="editor.chain().focus().toggleBold().run()"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('italic') }]"
          class="italic"
          @click="editor.chain().focus().toggleItalic().run()"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('underline') }]"
          class="underline"
          @click="editor.chain().focus().toggleUnderline().run()"
          title="Underline"
        >
          U
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('strike') }]"
          class="line-through"
          @click="editor.chain().focus().toggleStrike().run()"
          title="Strikethrough"
        >
          S
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('subscript') }]"
          @click="editor.chain().focus().toggleSubscript().run()"
          title="Subscript"
        >
          <span>X<sub>2</sub></span>
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('superscript') }]"
          @click="editor.chain().focus().toggleSuperscript().run()"
          title="Superscript"
        >
          <span>X<sup>2</sup></span>
        </button>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- Text color -->
        <div class="relative">
          <button type="button" :class="btn" @click="showTextColorPicker = !showTextColorPicker" title="Text Color">
            <span class="border-b-2" :style="{ borderColor: editor.getAttributes('textStyle').color || '#000' }"
              >A</span
            >
          </button>
          <div
            v-if="showTextColorPicker"
            class="absolute left-0 top-full z-10 mt-1 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
            style="display: grid; grid-template-columns: repeat(5, 28px); gap: 8px"
          >
            <button
              v-for="color in TEXT_COLORS"
              :key="color"
              type="button"
              class="h-7 w-7 cursor-pointer rounded border border-gray-200 transition-transform hover:scale-110"
              :style="{ backgroundColor: color }"
              @click="setTextColor(color)"
            />
          </div>
        </div>

        <!-- Highlight color -->
        <div class="relative">
          <button
            type="button"
            :class="[btn, { [btnActive]: editor.isActive('highlight') }]"
            @click="showHighlightColorPicker = !showHighlightColorPicker"
            title="Highlight"
          >
            <span class="bg-yellow-200 px-0.5">H</span>
          </button>
          <div
            v-if="showHighlightColorPicker"
            class="absolute left-0 top-full z-10 mt-1 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
            style="display: grid; grid-template-columns: repeat(5, 28px); gap: 8px"
          >
            <button
              v-for="color in HIGHLIGHT_COLORS"
              :key="color"
              type="button"
              class="h-7 w-7 cursor-pointer rounded border border-gray-200 transition-transform hover:scale-110"
              :style="{ backgroundColor: color }"
              @click="setHighlightColor(color)"
            />
          </div>
        </div>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- Alignment -->
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive({ textAlign: 'left' }) }]"
          @click="editor.chain().focus().setTextAlign('left').run()"
          title="Align Left"
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 10H3M21 6H3M21 14H3M17 18H3" />
          </svg>
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive({ textAlign: 'center' }) }]"
          @click="editor.chain().focus().setTextAlign('center').run()"
          title="Align Center"
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 10H6M21 6H3M21 14H3M18 18H6" />
          </svg>
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive({ textAlign: 'right' }) }]"
          @click="editor.chain().focus().setTextAlign('right').run()"
          title="Align Right"
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10H7M21 6H3M21 14H3M21 18H7" />
          </svg>
        </button>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- Lists -->
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('bulletList') }]"
          @click="editor.chain().focus().toggleBulletList().run()"
          title="Bullet List"
        >
          &bull;
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('orderedList') }]"
          @click="editor.chain().focus().toggleOrderedList().run()"
          title="Ordered List"
        >
          1.
        </button>
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('taskList') }]"
          @click="editor.chain().focus().toggleTaskList().run()"
          title="Task List"
        >
          &#9745;
        </button>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- Block elements -->
        <button
          type="button"
          :class="[btn, { [btnActive]: editor.isActive('blockquote') }]"
          @click="editor.chain().focus().toggleBlockquote().run()"
          title="Blockquote"
        >
          &ldquo;
        </button>
        <button
          type="button"
          :class="btn"
          @click="editor.chain().focus().setHorizontalRule().run()"
          title="Horizontal Rule"
        >
          &mdash;
        </button>
        <button type="button" :class="[btn, { [btnActive]: editor.isActive('link') }]" @click="setLink" title="Link">
          &#128279;
        </button>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- Insert -->
        <button type="button" :class="btn" @click="addImage" title="Insert Image">&#128247;</button>
        <button type="button" :class="btn" @click="addYoutube" title="Insert YouTube">&#9654;</button>
        <button type="button" :class="btn" @click="insertTable" title="Insert Table">&#9638;</button>

        <div class="mx-1 h-5 w-px bg-gray-300" />

        <!-- HTML source toggle -->
        <button
          type="button"
          :class="[btn, { [btnActive]: sourceMode }]"
          @click="toggleSourceMode"
          :title="sourceMode ? 'Switch to editor' : 'Edit HTML source'"
        >
          &lt;/&gt;
        </button>
      </div>

      <!-- Editor content (WYSIWYG or HTML source) -->
      <textarea
        v-if="sourceMode"
        v-model="sourceHtml"
        @input="onSourceInput"
        class="block min-h-[12rem] w-full px-4 py-3 font-mono text-sm text-gray-800 focus:outline-none"
        spellcheck="false"
      />
      <EditorContent v-else :editor="editor" />

      <!-- Character count -->
      <div v-if="editor" class="border-t border-gray-200 bg-gray-50 px-3 py-1 text-right text-xs text-gray-400">
        {{ editor.storage.characterCount.characters() }} characters &middot;
        {{ editor.storage.characterCount.words() }} words
      </div>
    </div>

    <p v-if="error" class="mt-1 text-sm text-red-500">{{ error }}</p>
  </div>
</template>

<style>
/* Task list checkbox styling */
.ProseMirror ul[data-type='taskList'] {
  list-style: none;
  padding-left: 0;
}
.ProseMirror ul[data-type='taskList'] li {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}
.ProseMirror ul[data-type='taskList'] li > label {
  flex-shrink: 0;
  margin-top: 0.25rem;
}

/* Table styling */
.ProseMirror table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
}
.ProseMirror th,
.ProseMirror td {
  border: 1px solid #d1d5db;
  padding: 0.5rem;
  min-width: 4rem;
}
.ProseMirror th {
  background-color: #f3f4f6;
  font-weight: 600;
}

/* Placeholder styling */
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

/* YouTube embed */
.ProseMirror iframe {
  max-width: 100%;
  border-radius: 0.375rem;
}
</style>
